"use client";

import type { AudioSample, ICmsLink, Media, OrbAudioPlayerBlock } from "@mw/shared/payload-types";

import SliderGeneric from "@ui/components/composers/SliderGenericWrapper";
import { AspectRatio } from "@ui/components/core/AspectRatio";
import { Link } from "@ui/components/core/Link";
import CoverArt from "@ui/components/routes/music/CoverArt";
import { MusicColorScheme } from "@ui/components/routes/music/colorScheme";
import { isValidLinkWithLabel } from "@ui/lib/link";
import { cn } from "@ui/lib/utils";
import { formatLink } from "@utilities/payload";
import { useCallback, useMemo, useState } from "react";

import { Block } from "./Block";

const CTA = ({ cta }: { cta?: ICmsLink }) => {
  const ctaLink = formatLink(cta);
  if (!ctaLink || !isValidLinkWithLabel(ctaLink)) return null;
  return (
    <Link {...ctaLink} asButton buttonVariants={{ variant: "primary" }} className='mt-24'>
      {ctaLink.label}
    </Link>
  );
};

const AudioPlayer = ({ audioSample, colorOverride }: SampleItem) => {
  const [audioElementAvailable, setAudioElementAvailable] = useState<HTMLAudioElement | null>(null);

  const stopAllOthers = useCallback(() => {
    document.querySelectorAll("audio").forEach(audio => {
      audio.pause();
    });
  }, []);

  const handlePlayPause = () => {
    if (audioElementAvailable) {
      if (audioElementAvailable.paused) {
        stopAllOthers();
        audioElementAvailable.play();
      } else {
        audioElementAvailable.pause();
      }
    }
  };

  const setAudioRef = (element: HTMLAudioElement | null) => {
    setAudioElementAvailable(element);
  };

  return (
    <>
      <CoverArt
        audioElement={audioElementAvailable}
        colorScheme={colorOverride || (audioSample.orbColor as MusicColorScheme) || MusicColorScheme.red}
        onClick={handlePlayPause}
        styles={audioSample.styleTags?.map(styleTag => styleTag.tag || "") || []}
        title={audioSample.title || ""}
      />

      <audio crossOrigin='anonymous' preload='none' ref={setAudioRef} src={(audioSample.audio as Media).url!} />
    </>
  );
};

interface SampleItem {
  audioSample: AudioSample;
  colorOverride?: MusicColorScheme;
}

export const BlockOrbAudioPlayerBlock = ({
  items,
  title,
  subtitle,
  cta,
  inContainer = true,
}: OrbAudioPlayerBlock & { inContainer?: boolean }) => {
  const sliderItems = useMemo(() => {
    if (!Array.isArray(items)) {
      return [];
    }
    return items
      ?.filter(item => !!item.audioSample && !!(item.audioSample as AudioSample).id)
      .map(item => {
        const sample = item as SampleItem;
        return (
          <AspectRatio className='mx-auto' key={sample.audioSample.id} ratio='1x1'>
            <AudioPlayer audioSample={sample.audioSample} colorOverride={sample.colorOverride} />
          </AspectRatio>
        );
      });
  }, [items]);

  return (
    <Block className={cn(inContainer && "container")} name='audio-player-with-transcript-slider'>
      <h2 className='f-heading-03'>{title}</h2>
      <p className='f-heading-03 mb-32'>{subtitle}</p>

      {sliderItems.length >= 3 ? (
        <SliderGeneric alignControls='start' cta={<CTA cta={cta} />} items={sliderItems} itemsPerViewportAtLg={3} />
      ) : (
        <div className='flex flex-col items-center'>
          <div className='flex w-full flex-col items-center justify-center gap-24 md:flex-row'>
            {sliderItems.map((item, index) => (
              <div className='max-w-[400px] flex-1' key={index}>
                {item}
              </div>
            ))}
          </div>
          <CTA cta={cta} />
        </div>
      )}
    </Block>
  );
};
